import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { sortBy } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';

import { buildStackedHorizontalBarOption } from 'shared/common/charts/options';

import { echarts, ReactEChartsCore } from '../common/charts/echarts-core';
import { usePortalTooltip } from '../common/charts/tooltip';
import {
    buildStackedTooltipFormatter,
    Field,
    funnelMetrics,
    getChartHeight,
    getFieldColors,
    getFieldLabel
} from '../common/metrics';
import { MetricsData } from '../graphql/queries/metrics';

interface JobEffortDialogProps {
    fields: Field[];
    jobId: string;
    jobLabel: string;
    metrics: MetricsData[];
    variant: 'cost' | 'count';
    onClose: () => void;
}

export const JobEffortDialog: React.FC<JobEffortDialogProps> = ({
    fields,
    jobId,
    jobLabel,
    metrics,
    variant,
    onClose
}) => {
    const chartModel = React.useMemo(() => {
        const rowsByUser = new Map<string, { rows: MetricsData[]; user: MetricsData['user'] }>();
        for (const metric of metrics) {
            const existing = rowsByUser.get(metric.user.id);
            if (existing) {
                existing.rows.push(metric);
            } else {
                rowsByUser.set(metric.user.id, { rows: [metric], user: metric.user });
            }
        }
        const entries = Array.from(rowsByUser.values()).map(({ rows, user }) => {
            const userMetrics = funnelMetrics(rows, variant);
            const sum = fields.reduce((acc, field) => acc + (userMetrics[field] ?? 0), 0);
            return { metrics: userMetrics, sum, user };
        });
        const sortedEntries = sortBy(entries, [(entry) => -entry.sum, (entry) => entry.user.name]);
        const categories = sortedEntries.map((entry) => entry.user.name);
        const series = fields.map((field) => ({
            data: sortedEntries.map((entry) => entry.metrics[field] ?? 0),
            name: getFieldLabel(field)
        }));
        return { categories, entries: sortedEntries, series };
    }, [fields, metrics, variant]);

    const tooltipFormatter = React.useMemo(() => {
        return buildStackedTooltipFormatter({
            entries: chartModel.entries.map((entry) => ({ label: entry.user.name, metrics: entry.metrics })),
            fields,
            header: 'Recruiter',
            percentSuffix: ' on this job',
            series: chartModel.series
        });
    }, [chartModel, fields]);

    const tooltip = usePortalTooltip(
        {
            formatter: tooltipFormatter,
            trigger: 'item'
        },
        [tooltipFormatter]
    );

    const colors = React.useMemo(() => getFieldColors(fields), [fields]);

    const chartOptions = React.useMemo(() => {
        if (!chartModel.categories.length) {
            return undefined;
        }
        return buildStackedHorizontalBarOption({
            categories: chartModel.categories,
            colors,
            series: chartModel.series,
            tooltip
        });
    }, [chartModel, colors, tooltip]);

    const chart = chartOptions ? (
        <ReactEChartsCore
            echarts={echarts}
            option={chartOptions}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: getChartHeight(chartModel.categories.length) }}
            opts={{ renderer: 'svg' }}
        />
    ) : (
        <Typography variant="body2">No activity for this job in the selected period</Typography>
    );

    const jobLink = (
        <Link to={`/job/${jobId}/board`}>
            <Button color="primary">Open Job</Button>
        </Link>
    );

    const metricsLink = (
        <Link to={`/job/${jobId}/metrics`}>
            <Button color="primary">Full Job Metrics</Button>
        </Link>
    );

    return (
        <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="md">
            <DialogTitle>{jobLabel}</DialogTitle>
            <DialogContent>{chart}</DialogContent>
            <DialogActions>
                {jobLink}
                {metricsLink}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
